import { validateAndTrackEntryPagesEvent } from '@vp/entry-pages-tracking-helpers'
import type { Logger } from '@vp/ubik-logging'
import type { TrackingLinkConfig, MapTrackingConfig } from '../types/homepageBanner'
import { trackMapEvent } from './mapTracking'
import { PAGE_NAME } from './constants'
import type { CtaTrackingConfig } from './tracking/types'
import { trackRuntimeEvent } from '@vp/ubik-context'
import type { ExperimentTrackingConfig, PersonalizationTrackingConfig, TrackUsedContextProps } from '../types/sharedTypes'

type LinkClickHandler = (event: React.MouseEvent<Element, MouseEvent>) => void

const DELAY_BEFORE_NAVIGATING_IN_MS = 300

export const getUrlIdForTracking = (urlData: { linkType?: string; trackingId?: string; actionId?: string } | null | undefined) => {
  if (!urlData) return null
  return urlData.linkType === 'External' ? urlData.trackingId : urlData.actionId
}

const createOnClickTrackingHandler = (
  trackingConfig: TrackingLinkConfig | undefined,
  logger: Logger,
  schema: object,
  href?: string,
  openLinkNewTab?: boolean,
  callback?: () => void,
  mapTrackingConfig?: MapTrackingConfig,
  mapSchema?: object,
  ubikEventTriggerId?: string,
  experimentTrackingConfig?: ExperimentTrackingConfig,
  experimentSchema?: object,
  personalizationTrackingConfig?: PersonalizationTrackingConfig,
  personalizationSchema?: object,
): LinkClickHandler | undefined => {
  if (!trackingConfig) {
    return undefined
  }
  return (event) => {
    event.stopPropagation()
    stopNavigation(event)
    validateAndTrackEntryPagesEvent(trackingConfig.eventName, trackingConfig.properties, schema, logger)

    if (ubikEventTriggerId) {
      trackRuntimeEvent(ubikEventTriggerId, { navigationDetail: trackingConfig.properties.navigationDetail })
    }

    if (mapTrackingConfig && mapSchema) {
      const { pageZone, placementVariantId, placementDefinitionId } = mapTrackingConfig
      trackMapEvent(pageZone, placementVariantId, placementDefinitionId, 'click', logger, mapSchema)
    }

    if (experimentTrackingConfig && experimentSchema) {
      validateAndTrackEntryPagesEvent('Experiment Clicked', experimentTrackingConfig, experimentSchema, logger)
    }

    // TO DO - remove when ubik personalization click tracking works
    if (personalizationTrackingConfig && personalizationSchema) {
      validateAndTrackEntryPagesEvent('Personalization Clicked', personalizationTrackingConfig, personalizationSchema, logger)
    }

    if (href) {
      setTimeout(() => navigate(href, openLinkNewTab), DELAY_BEFORE_NAVIGATING_IN_MS)
    }

    if (callback) {
      setTimeout(callback, DELAY_BEFORE_NAVIGATING_IN_MS)
    }
  }
}

export const getCtaTrackingConfig = ({
  destinationUrl,
  pageZone,
  linkIdentifier,
  ctaPosition,
  usedContext,
}: {
  destinationUrl: string
  pageZone: string
  linkIdentifier: string
  ctaPosition: number | string
  usedContext?: TrackUsedContextProps
}) => {
  const trimmedLinkIdentifier = linkIdentifier?.trim()
  const sourcePagePath = typeof window !== 'undefined' ? window.location.pathname : '/'
  const parsedDestinationPagePath = removeQueryParamsFrom(destinationUrl)
  const eventDetail = `${sourcePagePath};${parsedDestinationPagePath};${pageZone};${trimmedLinkIdentifier}`
  const navigationDetail =
    ctaPosition && ((typeof ctaPosition === 'number' && ctaPosition > 0) || typeof ctaPosition === 'string')
      ? `${trimmedLinkIdentifier}:${ctaPosition}`
      : trimmedLinkIdentifier
  return {
    eventName: 'Navigation Clicked',
    properties: {
      category: PAGE_NAME,
      label: `Content:${pageZone}`,
      eventDetail,
      navigationDetail,
    },
    linkAttributes: {
      'data-section': `Content:${pageZone}`,
      'data-position': ctaPosition,
      'data-translation': trimmedLinkIdentifier,
    },
    usedContext,
  }
}

const trackingOutput = (ctaTrackingConfig: CtaTrackingConfig): TrackingLinkConfig => {
  const { destinationPagePath, ctaValue, mpvId, productKey, subSection, sourcePagePath, position, pageZone } = ctaTrackingConfig
  const { experimentId, experimentKey, variationId, variationKey } = ctaTrackingConfig
  const parsedDestinationPagePath = removeQueryParamsFrom(destinationPagePath)
  const composedCtaValue = subSection ? `${subSection}:${ctaValue}` : ctaValue
  const eventDetail = `${sourcePagePath};${parsedDestinationPagePath};${pageZone};${composedCtaValue}`
  const navigationDetail = `${composedCtaValue}:${position}`

  const eventName = 'Navigation Clicked'
  const experimentProperties =
    experimentId && variationId
      ? {
          experimentId: experimentId.toString(),
          experimentName: experimentKey,
          variationId: variationId.toString(),
          variationName: variationKey,
        }
      : {}

  return {
    eventName,
    properties: {
      category: 'Home Page',
      label: `Content:${pageZone}`,
      eventDetail,
      navigationDetail: pageZone === 'Hero' ? ctaValue : navigationDetail,
      product_id: mpvId,
      core_product_id: productKey,
      ...experimentProperties,
    },
    linkAttributes: {
      'data-section': `Content:${pageZone}`,
      'data-position': position,
      'data-translation': composedCtaValue,
    },
  }
}

export const getSeoZoneCtaTrackingConfig = (sourcePagePath: string, destinationPagePath: string, ctaValue: string, position: number): TrackingLinkConfig => {
  const pageZone = 'SEO Zone'
  const subSection = undefined

  return trackingOutput({ sourcePagePath, destinationPagePath, pageZone, ctaValue, subSection, position })
}

export const getTileCtaTrackingConfig = (destinationUrl, pageZone, linkIdentifier, ctaPosition) => {
  return getCtaTrackingConfig({ destinationUrl, pageZone, linkIdentifier, ctaPosition })
}

const navigate = (href: string, openLinkNewTab?: boolean): void => {
  if (!openLinkNewTab) {
    window.location.href = href
  } else {
    window.open(href, '_blank')
  }
}
const stopNavigation = (event: React.MouseEvent<Element, MouseEvent>): void => event.preventDefault()

export const removeQueryParamsFrom = (pagePath: string): string => {
  if (typeof window !== 'undefined') {
    return new URL(pagePath, window.location.origin).pathname
  }

  return pagePath
}

export { createOnClickTrackingHandler }
